import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Building2, MapPin, Hash, Loader2, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function BranchesPage() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [schoolTypes, setSchoolTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", location: "", school_type: "" });

  async function load() {
    const [b, s] = await Promise.all([api.get("/branches"), api.get("/meta/school-types")]);
    setBranches(b.data);
    setSchoolTypes(s.data);
  }
  useEffect(() => { load().catch(() => {}); }, []);

  async function save(e) {
    e.preventDefault();
    if (!form.name || !form.location || !form.school_type) return;
    setSaving(true);
    try {
      const { data } = await api.post("/branches", form);
      toast.success(`Branch created: ${data.code}`);
      setOpen(false);
      setForm({ name: "", location: "", school_type: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function remove(b) {
    if (!window.confirm(`Delete branch ${b.code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/branches/${b.id}`);
      toast.success("Branch deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  const canManage = user?.role === "master_admin" || user?.role === "super_admin";
  const canDelete = user?.role === "master_admin";

  return (
    <AppShell>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-7">
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-violet-700 mb-2">Multi-Branch Setup</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Branches</h1>
          <p className="text-slate-500 mt-1.5">All physical campuses across Spark Family Enterprises.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-branch-button" className="bg-violet-600 hover:bg-violet-700 h-11 px-5 font-semibold">
                <Plus size={18} className="mr-2" /> Add Branch
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display text-xl">Add a new branch</DialogTitle>
              </DialogHeader>
              <form onSubmit={save} className="space-y-4 pt-2">
                <div>
                  <Label>Branch Name</Label>
                  <Input
                    data-testid="branch-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="SPARKORA Chennai T.Nagar"
                    className="mt-1.5 input-focus"
                    required
                  />
                </div>
                <div>
                  <Label>Location</Label>
                  <Input
                    data-testid="branch-location-input"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="T.Nagar, Chennai"
                    className="mt-1.5 input-focus"
                    required
                  />
                </div>
                <div>
                  <Label>School Type</Label>
                  <Select value={form.school_type} onValueChange={(v) => setForm({ ...form, school_type: v })}>
                    <SelectTrigger data-testid="branch-school-type-select" className="mt-1.5">
                      <SelectValue placeholder="Choose a school type" />
                    </SelectTrigger>
                    <SelectContent>
                      {schoolTypes.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-slate-500">A branch code like <span className="font-semibold text-violet-700">SPK-00X</span> will be auto-generated.</p>
                <Button
                  data-testid="save-branch-submit"
                  type="submit"
                  disabled={saving}
                  className="w-full h-11 bg-violet-600 hover:bg-violet-700"
                >
                  {saving ? <Loader2 className="animate-spin" size={18} /> : "Create Branch"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {branches.length === 0 ? (
        <Card className="border-dashed border-2 border-violet-200 bg-white">
          <CardContent className="py-16 text-center">
            <Building2 className="mx-auto text-violet-400" size={44} />
            <h3 className="font-display text-xl font-bold text-slate-900 mt-4">No branches yet</h3>
            <p className="text-slate-500 text-sm mt-1">Click <em>Add Branch</em> to spin up your first campus.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
          {branches.map((b) => {
            const st = schoolTypes.find((s) => s.id === b.school_type);
            return (
              <Card key={b.id} data-testid={`branch-card-${b.code}`} className="border-violet-100 hover:border-violet-400 hover:shadow-lg transition-all overflow-hidden group">
                <div className="bg-gradient-to-br from-violet-600 to-purple-700 p-5 text-white">
                  <div className="flex items-center justify-between">
                    <div className="h-10 w-10 rounded-lg bg-white/15 backdrop-blur flex items-center justify-center">
                      <Building2 size={20} />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider bg-white/15 px-2.5 py-1 rounded-full">
                      <Hash size={10} className="inline mb-0.5" /> {b.code}
                    </span>
                  </div>
                  <h3 className="font-display text-lg font-bold mt-4 leading-snug">{b.name}</h3>
                  <div className="flex items-center gap-1.5 text-violet-100 text-sm mt-1">
                    <MapPin size={13} /> {b.location}
                  </div>
                </div>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">School Type</div>
                    <div className="text-sm font-semibold text-slate-800 mt-0.5">{st?.name || b.school_type}</div>
                  </div>
                  {canDelete && (
                    <button
                      data-testid={`delete-branch-${b.code}`}
                      onClick={() => remove(b)}
                      className="h-9 w-9 rounded-lg border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-300 hover:bg-red-50 flex items-center justify-center transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}
